'use client';

import { useCallback, useSyncExternalStore } from 'react';
import {
  quietRefreshEscalates,
  refreshResultApplies,
  shouldRefreshOnPing,
  shouldReportQuietRefreshFailure,
} from '@/lib/pool-stream-health';
import { recordClientTelemetryEvent } from '@/lib/client-telemetry';
import type { PooledDialerPoolView } from '../../../shared/types/pooled-dialer';

const RECONNECT_MS = 5000;
const FALLBACK_POLL_MS = 2000;
// The server sends a named `ping` event every 15s so clients can tell a
// quiet-but-healthy stream from a half-open socket (EventSource fires no
// error on those — a silently dead connection would freeze the pool view
// forever while call audio keeps flowing). Three missed pings = stale.
const STALE_AFTER_MS = 45_000;
const WATCHDOG_INTERVAL_MS = 10_000;

interface PoolSnapshot {
  pool: PooledDialerPoolView | null;
  live: boolean;
  /** When `pool` content was last applied (event or poll); 0 before first data.
   * Consumers use it to refuse to render a long-dead snapshot as the floor. */
  updatedAt: number;
  /** Consecutive quiet-refresh failures on the current connection; resets on
   * any successful refresh or real `pool` event. Published so the UI can gate
   * its content-age watermark on proven pull failure — snapshot age alone is
   * what a healthy lull looks like. */
  quietRefreshFailures: number;
}

interface PoolStream {
  campaignId: string;
  snapshot: PoolSnapshot;
  listeners: Set<() => void>;
  source: EventSource | null;
  pollTimer: ReturnType<typeof setInterval> | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  watchdogTimer: ReturnType<typeof setInterval> | null;
  lastEventAt: number;
  lastPoolEventAt: number;
  refreshInFlight: boolean;
  quietRefreshReportedAt: number;
}

const EMPTY_SNAPSHOT: PoolSnapshot = { pool: null, live: false, updatedAt: 0, quietRefreshFailures: 0 };
const streams = new Map<string, PoolStream>();

function getStream(campaignId: string): PoolStream {
  const existing = streams.get(campaignId);
  if (existing) return existing;
  const stream: PoolStream = {
    campaignId,
    snapshot: EMPTY_SNAPSHOT,
    listeners: new Set(),
    source: null,
    pollTimer: null,
    reconnectTimer: null,
    watchdogTimer: null,
    lastEventAt: 0,
    lastPoolEventAt: 0,
    refreshInFlight: false,
    quietRefreshReportedAt: 0,
  };
  streams.set(campaignId, stream);
  return stream;
}

function update(stream: PoolStream, snapshot: PoolSnapshot): void {
  if (
    stream.snapshot.pool === snapshot.pool
    && stream.snapshot.live === snapshot.live
    && stream.snapshot.updatedAt === snapshot.updatedAt
    && stream.snapshot.quietRefreshFailures === snapshot.quietRefreshFailures
  ) return;
  stream.snapshot = snapshot;
  for (const listener of stream.listeners) listener();
}

function setQuietRefreshFailures(stream: PoolStream, count: number): void {
  update(stream, { ...stream.snapshot, quietRefreshFailures: count });
}

function stopPolling(stream: PoolStream): void {
  if (!stream.pollTimer) return;
  clearInterval(stream.pollTimer);
  stream.pollTimer = null;
}

async function poll(stream: PoolStream): Promise<void> {
  try {
    const response = await fetch(`/api/campaigns/${stream.campaignId}/pool/status`, { credentials: 'include' });
    if (!response.ok) return;
    const data = (await response.json()) as { pool?: PooledDialerPoolView | null };
    if (stream.listeners.size === 0 || stream.snapshot.live) return;
    update(stream, { ...stream.snapshot, pool: data.pool ?? null, live: false, updatedAt: Date.now() });
  } catch (error) {
    console.warn('pool status poll failed', error);
  }
}

// One-shot self-heal for a live-but-quiet stream (card 472): pings prove the
// transport, but a `pool` silence this long means the subscription stopped
// delivering — the frozen snapshot keeps a wrap-up countdown and an "Up next"
// promise on screen that the real floor abandoned minutes ago. The regular
// fallback poll is (rightly) gated off while `live`, so this path refreshes
// explicitly; a genuine `pool` event racing the fetch wins.
async function refreshQuietStream(stream: PoolStream): Promise<void> {
  stream.refreshInFlight = true;
  // The connection whose quiet window owed this refresh — escalation must not
  // tear down a successor connection the failure says nothing about.
  const source = stream.source;
  const startedAt = Date.now();
  let fetched = false;
  try {
    const response = await fetch(`/api/campaigns/${stream.campaignId}/pool/status`, { credentials: 'include' });
    if (response.ok) {
      const data = (await response.json()) as { pool?: PooledDialerPoolView | null };
      // The fetch proves the view is healable even when its result loses the
      // race below — only what gets RENDERED must respect event ordering.
      fetched = true;
      if (stream.listeners.size > 0 && refreshResultApplies(stream.lastPoolEventAt, startedAt)) {
        update(stream, { ...stream.snapshot, pool: data.pool ?? null, updatedAt: Date.now(), quietRefreshFailures: 0 });
      }
    }
  } catch (error) {
    console.warn('pool quiet-stream refresh failed', error);
  } finally {
    // The refresh settles the quiet-stream debt whatever its outcome: a
    // legitimately quiet pool (paused campaign, off-hours, no pool at all)
    // would otherwise stay past the threshold forever and turn this one-shot
    // heal into a permanent poll on every ping — one /pool/status hit per
    // client per 15s. A failure waits out a full quiet window before the
    // next attempt rather than retrying at ping cadence.
    stream.lastPoolEventAt = Date.now();
    stream.refreshInFlight = false;
    if (fetched) {
      setQuietRefreshFailures(stream, 0);
    } else {
      onQuietRefreshFailed(stream, source);
    }
  }
}

// A failed self-heal used to just re-arm — invisible, and unbounded (the Sep 4
// incident: a saturated uplink let 15s SSE pings through while every status
// fetch timed out, so a live-badged frozen view survived for 15 minutes).
// Now each failure is reported, and a repeat proves the stream unhealable in
// place: tear it down so the dead-transport path (poll, reconnect, stale-UI
// gate) owns the screen instead.
function onQuietRefreshFailed(stream: PoolStream, source: EventSource | null): void {
  // A fetch that outlived its connection proves nothing about the successor:
  // it must neither seed the new connection's failure streak nor tear it down.
  if (stream.source !== source) return;
  const failures = stream.snapshot.quietRefreshFailures + 1;
  setQuietRefreshFailures(stream, failures);
  const now = Date.now();
  if (shouldReportQuietRefreshFailure(now, failures, stream.quietRefreshReportedAt)) {
    stream.quietRefreshReportedAt = now;
    recordClientTelemetryEvent('pool_quiet_refresh_failed', 'warning', {
      consecutiveFailures: failures,
      secondsSinceSnapshot:
        stream.snapshot.updatedAt > 0 ? Math.round((now - stream.snapshot.updatedAt) / 1000) : null,
    });
  }
  if (quietRefreshEscalates(failures) && source) {
    markDisconnected(stream, source);
  }
}

function startPolling(stream: PoolStream): void {
  if (stream.pollTimer) return;
  void poll(stream);
  stream.pollTimer = setInterval(() => void poll(stream), FALLBACK_POLL_MS);
}

function stopWatchdog(stream: PoolStream): void {
  if (!stream.watchdogTimer) return;
  clearInterval(stream.watchdogTimer);
  stream.watchdogTimer = null;
}

// Shared teardown for an explicit error and a stale (half-open) connection:
// drop to polling and schedule a fresh EventSource.
function markDisconnected(stream: PoolStream, source: EventSource): void {
  if (stream.source !== source) return;
  source.close();
  stream.source = null;
  stopWatchdog(stream);
  // Keep the last pool and its ORIGINAL updatedAt: the content is not newer
  // for having been disconnected, and consumers age it to decide when the
  // frozen view must stop being rendered as the floor. The failure streak
  // belongs to the connection being torn down and does not carry over.
  update(stream, { ...stream.snapshot, live: false, quietRefreshFailures: 0 });
  startPolling(stream);
  stream.reconnectTimer ??= setTimeout(() => {
    stream.reconnectTimer = null;
    connect(stream);
  }, RECONNECT_MS);
}

function connect(stream: PoolStream): void {
  if (stream.listeners.size === 0 || stream.source) return;
  const source = new EventSource(`/api/campaigns/${stream.campaignId}/pool/stream`);
  stream.source = source;
  stream.lastEventAt = Date.now();
  // A dead connection fires no error event, so liveness has to be observed:
  // any message (snapshot or the server's 15s ping) counts, and the watchdog
  // recycles the connection once the stream has been silent too long — which
  // also catches a tab waking from sleep with a long-stale view.
  stream.watchdogTimer = setInterval(() => {
    if (stream.source !== source) return;
    if (Date.now() - stream.lastEventAt > STALE_AFTER_MS) {
      markDisconnected(stream, source);
    }
  }, WATCHDOG_INTERVAL_MS);
  source.addEventListener('pool', (event: MessageEvent<string>) => {
    stream.lastEventAt = Date.now();
    stream.lastPoolEventAt = stream.lastEventAt;
    try {
      const payload = JSON.parse(event.data) as { pool: PooledDialerPoolView | null };
      // Real data flowing again also ends any quiet-refresh failure episode.
      update(stream, { pool: payload.pool, live: true, updatedAt: Date.now(), quietRefreshFailures: 0 });
    } catch (error) {
      console.error('Invalid pooled-dialer stream payload', error);
    }
  });
  source.addEventListener('ping', () => {
    stream.lastEventAt = Date.now();
    if (shouldRefreshOnPing(stream.lastEventAt, stream.lastPoolEventAt, stream.refreshInFlight)) {
      void refreshQuietStream(stream);
    }
  });
  source.onopen = () => {
    if (stream.source !== source) return;
    stream.lastEventAt = Date.now();
    stopPolling(stream);
    if (stream.reconnectTimer) {
      clearTimeout(stream.reconnectTimer);
      stream.reconnectTimer = null;
    }
  };
  source.onerror = () => {
    markDisconnected(stream, source);
  };
}

function subscribe(campaignId: string, listener: () => void): () => void {
  const stream = getStream(campaignId);
  stream.listeners.add(listener);
  connect(stream);
  return () => {
    stream.listeners.delete(listener);
    if (stream.listeners.size > 0) return;
    stream.source?.close();
    stream.source = null;
    stopWatchdog(stream);
    stopPolling(stream);
    if (stream.reconnectTimer) clearTimeout(stream.reconnectTimer);
    streams.delete(campaignId);
  };
}

export function usePoolStream(campaignId: string | null): PoolSnapshot {
  const subscribeToCampaign = useCallback(
    (listener: () => void) => campaignId ? subscribe(campaignId, listener) : () => undefined,
    [campaignId]
  );
  const getSnapshot = useCallback(
    () => campaignId ? (streams.get(campaignId)?.snapshot ?? EMPTY_SNAPSHOT) : EMPTY_SNAPSHOT,
    [campaignId]
  );
  return useSyncExternalStore(subscribeToCampaign, getSnapshot, () => EMPTY_SNAPSHOT);
}
